import React from 'react';
import { 
  ArrowDownTrayIcon, 
  WalletIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon,
  ShareIcon,
  EllipsisHorizontalIcon,
  CloudIcon
} from '@heroicons/react/24/outline';

export default function Dashboard() {
  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="flex-1 px-8 md:px-12 py-10 overflow-y-auto">
        <div className="max-w-[1300px] mx-auto flex flex-col gap-8">
          
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h1 className="text-[34px] font-extrabold text-slate-900 tracking-tight">Dashboard</h1>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors shadow-sm">
                <ArrowDownTrayIcon className="w-4 h-4 text-slate-500" /> Export Data
              </button>
              <button className="bg-brand-500 text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors shadow-sm shadow-brand-500/20">
                Run Analysis
              </button>
            </div>
          </div>

          {/* Top Metrics Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Credit Balance Card */}
            <div className="bg-brand-500 rounded-[24px] p-8 text-white shadow-lg shadow-brand-500/20 relative overflow-hidden flex flex-col justify-between min-h-[300px]">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl pointer-events-none transform translate-x-10 -translate-y-10"></div>
              
              <div className="flex items-center justify-between relative z-10">
                <div className="w-10 h-10 border border-white/20 rounded-[10px] flex items-center justify-center backdrop-blur-sm">
                  <WalletIcon className="w-5 h-5 text-white" />
                </div>
                <span className="px-3.5 py-1.5 bg-white/10 border border-white/10 rounded-full text-[10px] font-extrabold tracking-widest uppercase backdrop-blur-sm">
                  Elite Status
                </span>
              </div>
              
              <div className="relative z-10 mt-auto">
                <p className="text-white/90 text-[14px] font-medium tracking-wide mb-1">Available Credit Balance</p>
                <div className="flex items-baseline gap-2.5 mb-3">
                  <span className="text-[52px] font-extrabold tracking-tight leading-none">1,248</span>
                  <span className="text-white/90 font-medium text-[16px]">Credits</span>
                </div>
                <div className="flex items-center gap-1.5 text-[12px] font-bold text-white/90 tracking-wide">
                  <ArrowTrendingUpIcon className="w-4 h-4 stroke-2" /> +12.5% from last month
                </div>
              </div>
            </div>

            {/* Trends Chart Card */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col min-h-[300px] overflow-hidden relative">
              <div className="flex items-center justify-between mb-2 z-10 relative">
                <h3 className="text-[17px] font-extrabold text-slate-900 tracking-tight">Search Activity Trends</h3>
                <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase">
                  <button className="px-3 py-1.5 text-slate-400 hover:text-slate-900 transition-colors rounded-lg">7 Days</button>
                  <button className="px-3 py-1.5 bg-brand-100/50 text-brand-600 rounded-lg">30 Days</button>
                </div>
              </div>
              
              {/* Chart Container */}
              <div className="flex-1 relative w-full flex items-end mt-4">
                <svg 
                  className="w-full h-full overflow-visible" 
                  viewBox="0 0 1000 250" 
                  preserveAspectRatio="none"
                >
                  <defs>
                    <linearGradient id="curveGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0054D6" stopOpacity="0.15" />
                      <stop offset="100%" stopColor="#0054D6" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Gradient Fill */}
                  <path 
                    d="M 20 200 C 150 80, 250 140, 350 140 C 450 140, 550 40, 650 40 C 750 40, 800 220, 850 220 C 900 220, 950 100, 980 60 L 980 250 L 20 250 Z" 
                    fill="url(#curveGradient)" 
                  />

                  {/* Smooth Stroke Line */}
                  <path 
                    d="M 20 200 C 150 80, 250 140, 350 140 C 450 140, 550 40, 650 40 C 750 40, 800 220, 850 220 C 900 220, 950 100, 980 60" 
                    fill="none" 
                    stroke="#0054D6" 
                    strokeWidth="6" 
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                  />
                  {/* Floating Data Point Dot */}
                  <circle cx="500" cy="80" r="6" fill="#0054D6" vectorEffect="non-scaling-stroke" />
                </svg>
              </div>

              {/* X Axis */}
              <div className="flex justify-between items-center mt-4 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-2 z-10 relative">
                <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
              </div>
            </div>
          </div>

          {/* Recent Search Analysis Table */}
          <div className="bg-white border border-slate-200 rounded-[24px] shadow-sm flex flex-col overflow-hidden">
            <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-[17px] font-extrabold text-slate-900 tracking-tight">Recent Search Analysis</h3>
              <div className="flex items-center gap-5 text-[12px] font-bold text-slate-500">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-brand-500"></div> High Precision
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-slate-500"></div> Deep Crawl
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50/50 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.15em] border-b border-slate-100">
                    <th className="px-8 py-5">Search Query</th>
                    <th className="px-8 py-5">Type of Search</th>
                    <th className="px-8 py-5">Confidence</th>
                    <th className="px-8 py-5">Status</th>
                    <th className="px-8 py-5 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {/* Row 1 */}
                  <tr className="hover:bg-slate-50 transition-colors group">
                    <td className="px-8 py-5">
                      <p className="text-[14px] font-extrabold text-slate-900 mb-0.5">LLM Market Share Analysis 2024</p>
                      <p className="text-[11px] text-slate-400 font-medium">UUID: cur_834_92i</p>
                    </td>
                    <td className="px-8 py-5">
                      <span className="px-4 py-1.5 bg-brand-100/50 text-brand-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full">Normal</span>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex flex-col gap-1.5 w-32">
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="w-[99.8%] h-full bg-brand-500 rounded-full"></div>
                        </div>
                        <span className="text-[11px] font-extrabold text-slate-900">99.8%</span>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                        <span className="text-[13px] font-medium text-slate-700">Complete</span>
                      </div>
                    </td>
                    <td className="px-8 py-5 text-center">
                      <button className="text-slate-400 hover:text-brand-500 transition-colors"><EllipsisHorizontalIcon className="w-6 h-6 mx-auto" /></button>
                    </td>
                  </tr>

                  {/* Row 2 */}
                  <tr className="hover:bg-slate-50 transition-colors group">
                    <td className="px-8 py-5">
                      <p className="text-[14px] font-extrabold text-slate-900 mb-0.5">Global Semiconductor Supply Chain</p>
                      <p className="text-[11px] text-slate-400 font-medium">UUID: cur_102_45x</p>
                    </td>
                    <td className="px-8 py-5">
                      <span className="px-4 py-1.5 bg-purple-100/50 text-purple-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full">Deep Search</span>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex flex-col gap-1.5 w-32">
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="w-[85.4%] h-full bg-slate-700 rounded-full"></div>
                        </div>
                        <span className="text-[11px] font-extrabold text-slate-900">85.4%</span>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-brand-500"></div>
                        <span className="text-[13px] font-medium text-slate-700">Analyzing</span>
                      </div>
                    </td>
                    <td className="px-8 py-5 text-center">
                      <button className="text-slate-400 hover:text-brand-500 transition-colors"><EllipsisHorizontalIcon className="w-6 h-6 mx-auto" /></button>
                    </td>
                  </tr>

                  {/* Row 3 */}
                  <tr className="hover:bg-slate-50 transition-colors group">
                    <td className="px-8 py-5">
                      <p className="text-[14px] font-extrabold text-slate-900 mb-0.5">Quantum Computing Venture Flows</p>
                      <p className="text-[11px] text-slate-400 font-medium">UUID: cur_551_11z</p>
                    </td>
                    <td className="px-8 py-5">
                      <span className="px-4 py-1.5 bg-brand-100/50 text-brand-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full">Super Deep X1</span>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex flex-col gap-1.5 w-32">
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="w-[94.1%] h-full bg-brand-500 rounded-full"></div>
                        </div>
                        <span className="text-[11px] font-extrabold text-slate-900">94.1%</span>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                        <span className="text-[13px] font-medium text-slate-700">Complete</span>
                      </div>
                    </td>
                    <td className="px-8 py-5 text-center">
                      <button className="text-slate-400 hover:text-brand-500 transition-colors"><EllipsisHorizontalIcon className="w-6 h-6 mx-auto" /></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="px-8 py-5 border-t border-slate-100 flex justify-center bg-slate-50/50">
              <button className="text-[12px] font-extrabold text-brand-500 uppercase tracking-widest hover:text-brand-700 transition-colors">
                View All Activity
              </button>
            </div>
          </div>

          {/* Bottom Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-10">
            
            <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col">
              <div className="mb-5">
                <CloudIcon className="w-6 h-6 text-brand-500" strokeWidth={2} />
              </div>
              <h4 className="text-[15px] font-extrabold text-slate-900 mb-2">Search Velocity</h4>
              <p className="text-[13px] text-slate-500 leading-relaxed mb-6 font-medium">
                You are searching 40% faster than your segment average. Upgrade to Apex for even lower latency.
              </p>
              <div className="w-full h-[6px] bg-slate-100 rounded-full overflow-hidden mt-auto">
                <div className="w-[60%] h-full bg-brand-500 rounded-full"></div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col">
              <div className="mb-5">
                <ShieldCheckIcon className="w-6 h-6 text-slate-600" strokeWidth={2} />
              </div>
              <h4 className="text-[15px] font-extrabold text-slate-900 mb-2">Security Perimeter</h4>
              <p className="text-[13px] text-slate-500 leading-relaxed font-medium">
                All data streams are encrypted with RSA-4096. Your session is anchored in Zurich, Switzerland.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col">
              <div className="mb-5">
                <ShareIcon className="w-6 h-6 text-slate-600" strokeWidth={2} />
              </div>
              <h4 className="text-[15px] font-extrabold text-slate-900 mb-2">Network Nodes</h4>
              <p className="text-[13px] text-slate-500 leading-relaxed font-medium">
                12 Global Curators are currently operational. Dynamic load balancing is active across all regions.
              </p>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}